import { SignUp, useAuth, useClerk, useUser } from '@clerk/clerk-react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import NavBar from '@/components/nav-bar/nav-bar';
import { useTranslations } from '@/i18n/use-translations';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

function buildResumePath(contributorId: string | null, email: string | null): string {
  if (contributorId && email) {
    return `/contributions?contributorId=${encodeURIComponent(contributorId)}&email=${encodeURIComponent(email)}&type=contributor`;
  }
  return '/contributions';
}

/**
 * Clerk application-invitation landing: `redirectUrl` from createInvitation + `__clerk_ticket`.
 * Signed-out users complete sign-up via embedded `<SignUp />`; signed-in users choose to continue or sign out.
 */
export default function AcceptInvitationPage() {
  const t = useTranslations();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isLoaded, userId } = useAuth();
  const { user } = useUser();
  const { signOut } = useClerk();
  const [choiceOpen, setChoiceOpen] = useState(false);

  const ticket = searchParams.get('__clerk_ticket');
  const contributorId = searchParams.get('contributorId');
  const email = searchParams.get('email');
  const resumePath = buildResumePath(contributorId, email);

  useEffect(() => {
    if (isLoaded && userId && ticket) {
      setChoiceOpen(true);
    }
  }, [isLoaded, userId, ticket]);

  useEffect(() => {
    if (isLoaded && userId && !ticket) {
      void navigate(resumePath, { replace: true });
    }
  }, [isLoaded, userId, ticket, navigate, resumePath]);

  if (!isLoaded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (userId && ticket) {
    const primary = user?.primaryEmailAddress?.emailAddress ?? user?.emailAddresses?.[0]?.emailAddress;
    return (
      <>
        <NavBar />
        <Dialog open={choiceOpen} onClose={() => setChoiceOpen(false)}>
          <DialogTitle>{t('invitationFlow.acceptInvitationPage.dialogTitle')}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ pt: 1 }}>
              {primary
                ? t('invitationFlow.acceptInvitationPage.signedInWithEmail', { email: primary })
                : t('invitationFlow.acceptInvitationPage.signedInWithoutEmail')}
            </Typography>
            <Typography variant="body2" sx={{ mt: 2 }}>
              {t('invitationFlow.acceptInvitationPage.nextStepsHint')}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => {
                setChoiceOpen(false);
                navigate(resumePath);
              }}
              variant="contained"
            >
              {t('invitationFlow.acceptInvitationPage.continueWithCurrentAccount')}
            </Button>
            <Button
              onClick={() => {
                setChoiceOpen(false);
                void signOut({ redirectUrl: window.location.href });
              }}
            >
              {t('invitationFlow.acceptInvitationPage.signOut')}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  if (userId && !ticket) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!ticket) {
    return (
      <>
        <NavBar />
        <Typography sx={{ p: 3 }}>{t('invitationFlow.acceptInvitationPage.needsValidLink')}</Typography>
      </>
    );
  }

  return (
    <>
      <NavBar />
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, px: 2 }}>
        <SignUp routing="path" path="/accept-invitation" forceRedirectUrl={resumePath} signInUrl="/" />
      </Box>
    </>
  );
}
